import React, { useState, useEffect } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const ProgramView = ({ filePath, onClose, initialSearch = '' }) => {
    const [rawMarkdown, setRawMarkdown] = useState('')
    const [filteredContent, setFilteredContent] = useState('')
    const [query, setQuery] = useState('')
    const [stats, setStats] = useState([]) // 🔢 statystyki: fraza -> liczba wystąpień

    const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const highlightText = (text, keywords) => {
        if (!keywords.length) return text
        const safeKeywords = keywords.filter(Boolean).map(escapeReg)
        if (!safeKeywords.length) return text

        const regex = new RegExp(`(${safeKeywords.join('|')})`, 'gi')
        return text.replace(
            regex,
            (match) =>
                `<span class="bg-warning text-dark px-1 rounded">${match}</span>`
        )
    }

    // 🔍 Wspólna logika filtrowania + liczenia wystąpień
    const applySearch = (search, markdown) => {
        const searchQuery = (search || '').toLowerCase()
        setQuery(searchQuery)

        if (!markdown) {
            setFilteredContent('')
            setStats([])
            return
        }

        // 🔁 brak fraz → pokazujemy cały program
        if (!searchQuery.trim()) {
            setFilteredContent(DOMPurify.sanitize(marked(markdown)))
            setStats([])
            return
        }

        let keywords = []
        let matchAll = false

        if (searchQuery.includes('+') && !searchQuery.includes(';')) {
            // rejestr+grunt → AND
            keywords = searchQuery
                .split('+')
                .map((w) => w.trim())
                .filter(Boolean)
            matchAll = true
        } else {
            // domyślnie: frazy rozdzielone ; → OR
            keywords = searchQuery
                .split(';')
                .map((w) => w.trim())
                .filter(Boolean)
        }

        const lines = markdown.split('\n')

        const matchedLines = lines.filter((line) => {
            const low = line.toLowerCase()
            return matchAll
                ? keywords.every((k) => low.includes(k)) // AND
                : keywords.some((k) => low.includes(k)) // OR
        })

        const highlightedMarkdown = highlightText(
            matchedLines.join('\n'),
            keywords
        )

        setFilteredContent(DOMPurify.sanitize(marked(highlightedMarkdown)))

        // 🔢 statystyki: każdą frazę rozbijamy po ; i +
        const allTokens = Array.from(
            new Set(
                searchQuery
                    .split(/[;+]/)
                    .map((w) => w.trim())
                    .filter(Boolean)
            )
        )

        const lowerText = markdown.toLowerCase()
        const perToken = allTokens.map((tok) => {
            const re = new RegExp(escapeReg(tok), 'g')
            const matches = lowerText.match(re)
            return {
                keyword: tok,
                count: matches ? matches.length : 0,
            }
        })

        setStats(perToken)
    }

    const handleSearch = (e) => {
        applySearch(e.target.value, rawMarkdown)
    }

    // 📥 ładowanie programu z pliku
    useEffect(() => {
        fetch(filePath)
            .then((res) => res.text())
            .then((text) => {
                setRawMarkdown(text)
            })
            .catch((err) => console.error('Błąd ładowania programu:', err))
    }, [filePath])

    // 🔁 kiedy mamy markdown + initialSearch → od razu filtrujemy
    useEffect(() => {
        if (!rawMarkdown) return
        applySearch(initialSearch, rawMarkdown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSearch, rawMarkdown]) // applySearch zależy tylko od rawMarkdown/initialSearch, które są już w deps

    return (
        <>
            {/* Bootstrapowa ciemna nakładka */}
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="-1">
                <div className="modal-dialog custom-dialog modal-lg">
                    <div className="modal-content">
                        {/* Przypięta góra modala z zamykaniem */}
                        <div className="modal-header sticky-top shadow-sm">
                            <h5 className="modal-title">
                                📄 Program szkolenia
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {/* Przypięta wyszukiwarka + statsy */}
                            <div className="sticky-top py-2">
                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    placeholder="Rozdziel frazy ; żeby znaleźć jedno z słów, użyj + żeby znaleźć wszystkie"
                                    value={query}
                                    onChange={handleSearch}
                                />

                                {stats.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mb-2 small">
                                        {stats.map(({ keyword, count }) => (
                                            <span
                                                key={keyword}
                                                className="badge bg-secondary"
                                            >
                                                {keyword}{' '}
                                                <span className="badge bg-light text-dark ms-1">
                                                    {count}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Wyświetlenie przefiltrowanej zawartości */}
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: filteredContent,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProgramView
